/**
 * Shade size guide. Wires the "Size Guide" trigger to a native <dialog> so the
 * panel promotes to the top layer (escaping the sticky product-details column)
 * and inherits the platform's focus trap, Escape-to-close and inert background.
 *
 * Self-registering, idempotent and theme-editor safe: the element re-runs
 * connectedCallback when its section re-renders, and listeners are torn down on
 * disconnect so nothing doubles up. No motion is forced here; the open
 * transition lives behind a prefers-reduced-motion query in the block CSS.
 */
class ShadeSizeGuide extends HTMLElement {
  /** @type {{el: EventTarget, type: string, fn: EventListener}[]} */
  #listeners = [];

  connectedCallback() {
    const trigger = this.querySelector('[data-shade-size-guide-open]');
    const dialog = /** @type {HTMLDialogElement | null} */ (
      this.querySelector('[data-shade-size-guide-dialog]')
    );
    if (!trigger || !dialog) return;

    const closeBtn = dialog.querySelector('[data-shade-size-guide-close]');

    const open = () => {
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    };

    const close = () => {
      if (typeof dialog.close === 'function') {
        if (dialog.open) dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    };

    this.#on(trigger, 'click', open);
    if (closeBtn) this.#on(closeBtn, 'click', close);

    // Close on a pointer click outside the panel (the dimmed backdrop). Testing
    // the pointer against the panel rect is robust whether the panel fills the
    // dialog or leaves a gap. Keyboard-synthesized clicks (Enter/Space on a
    // focused control inside the panel) carry no pointer coordinates and would
    // otherwise read as "outside", so they are ignored via event.detail.
    const panel = dialog.querySelector('.shade-size-dialog__panel');
    this.#on(dialog, 'click', (event) => {
      if (event.detail === 0) return;
      if (!panel) {
        if (event.target === dialog) close();
        return;
      }
      const rect = panel.getBoundingClientRect();
      const inPanel =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inPanel) close();
    });
  }

  disconnectedCallback() {
    for (const { el, type, fn } of this.#listeners) el.removeEventListener(type, fn);
    this.#listeners = [];
  }

  /**
   * @param {EventTarget} el
   * @param {string} type
   * @param {EventListener} fn
   */
  #on(el, type, fn) {
    el.addEventListener(type, fn);
    this.#listeners.push({ el, type, fn });
  }
}

if (!customElements.get('shade-size-guide')) {
  customElements.define('shade-size-guide', ShadeSizeGuide);
}
